import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, StyleSheet, useWindowDimensions, View } from 'react-native';
import { ActivityIndicator, Card } from 'react-native-paper';
import { useTheme } from '@react-navigation/native';
import { createCommonStyles } from '../../../../styles/commonStyles';
import { SafeAreaView } from 'react-native-safe-area-context';
import CustomLabel from '../../../../components/CustomLabel/CustomLabel';
import { FlatList } from 'react-native-web';
import CustomButton from '../../../../components/CustomButton';
import { useTranslation } from 'react-i18next';
import { useCompany } from '../../../../hooks/useCompany';
import Animated from 'react-native-reanimated';
import { useFieldArray, useForm } from 'react-hook-form';
import CustomControlledCheckbox from '../../../../components/ControlledComponents/CustomControlledCheckbox';
import { v4 as uuidv4 } from 'uuid';
import CustomControlledDropdown from '../../../../components/ControlledComponents/CustomControlledDropdown';
import { useHRAdminEmployees } from '../../../../hooks/useHRAdminEmployees';
import { showMessage } from 'react-native-flash-message';

const ListView = React.memo(({ item, index, styles, handleDelete, employees, control, setValue }) => {
    return (
        <SafeAreaView style={styles.row}>
            <Animated.View
                style={[styles.item, { justifyContent: 'flex-start' }]}>
                <CustomControlledDropdown
                    multipleSelect={false}
                    control={control}
                    name={`reportsTo.${index}.ManagerID`}
                    type={'EDIT_EMPLOYEE'}
                    options={employees}
                    setValue={setValue}
                    disabled={false}
                // rules={field[1]}
                />
            </Animated.View>
            <Animated.View
                style={[styles.item]}>
                <CustomControlledCheckbox
                    control={control}
                    name={`reportsTo.${index}.Notifieduponapprovalonly`}
                    type={'EDIT_EMPLOYEE_CENTER'}
                />
            </Animated.View>
            <Animated.View
                style={[styles.item]}>
                <CustomControlledCheckbox
                    control={control}
                    name={`reportsTo.${index}.Isprimary`}
                    type={'EDIT_EMPLOYEE_CENTER'}
                />
            </Animated.View>
            <Animated.View
                style={[styles.item]}>
                <CustomControlledCheckbox
                    control={control}
                    name={`reportsTo.${index}.ApprovesLeaves`}
                    type={'EDIT_EMPLOYEE_CENTER'}
                />
            </Animated.View>
            <Animated.View
                style={[styles.item]}>
                <CustomControlledCheckbox
                    control={control}
                    name={`reportsTo.${index}.ApprovesTimesheets`}
                    type={'EDIT_EMPLOYEE_CENTER'}
                />
            </Animated.View>
            <SafeAreaView style={styles.margin}>
                <CustomButton
                    iconName='trash-outline'
                    onPress={() => {
                        handleDelete(index)
                    }}
                    show='HOME_BELL'
                    type='HOME_BELL'
                    textTop='2'
                    sz={30} />
            </SafeAreaView>
        </SafeAreaView>
    )
})

export default function HRAdminReportsTo(props) {
    const { colors } = useTheme();
    const isPlatformWeb = (Platform.OS === 'web' && width > 992 ? false : true);
    const common = createCommonStyles(colors);
    const styles = makeStyles(colors, width, height, isPlatformWeb);
    const { width, height } = useWindowDimensions();
    const { getAllEmployees } = useCompany();
    const { insertReportsToWizard } = useHRAdminEmployees();
    const [isLoading, setIsLoading] = useState(false);
    const [employees, setEmployees] = useState([]);
    const ref = useRef(null);
    const { t } = useTranslation();
    const { control, handleSubmit, formState: { errors }, setValue, getValues } = useForm({
        defaultValues: {
            reportsTo: [{
                ManagerID: null,
                Notifieduponapprovalonly: false,
                Isprimary: false,
                ApprovesLeaves: false,
                ApprovesTimesheets: false,
                rowguid: uuidv4(),
                employeeID: props?.employee.employeeID
            }],
        },
    });
    const { fields, prepend, remove } = useFieldArray({
        control,
        name: 'reportsTo'
    });

    //#region UseEffects
    useEffect(() => {
        setIsLoading(true);
        let company = props?.employee?.company ?? '';
        getAllEmployees(company)
            .then((data) => {
                if(!data){
                    showMessage({
                        message: t('common:loadManagersFailed'),
                        type: "error",
                        duration: 3000,
                    })
                }
                setEmployees([...data]);
                setIsLoading(false);
            }).catch(() => {
                setIsLoading(false);
            });
    }, [])
    //#endregion

    //#region Handlers
    const handleDelete = (index) => {
        remove(index);
    }

    const handleAddNew = () => {
        prepend({
            ManagerID: null,
            Notifieduponapprovalonly: false,
            Isprimary: false,
            ApprovesLeaves: false,
            ApprovesTimesheets: false,
            rowguid: uuidv4(),
            employeeID: props?.employee.employeeID
        })
    }

    const onSubmit = data => {
        const validationResult = validateForm(data);
        const isValid = validationResult?.isValid ?? false;
        if (isValid) {
            insertReportsToWizard(data).then((res) => {
                const status = res?.status;
                if (status === 200) {
                    props.changeStep();
                }
                else {
                    showMessage({
                        message: t('common:issueMessage'),
                        type: "error",
                        duration: 3000,
                    })
                }
            })
        }
        else {
            showMessage({
                message: t(`common:notValidReportsTo`),
                type: "danger",
                duration: 3000,
            });
        }
    };

    //#endregion

    //#region Helpers
    const validateForm = (data) => {
        const notifiedUponApprovalCount = data.reportsTo.filter(item => item.Notifieduponapprovalonly).length;
        const primaryCount = data.reportsTo.filter(item => item.Isprimary).length;
        const approvesLeavesCount = data.reportsTo.filter(item => item.ApprovesLeaves).length;
        const approvesTimesheetsCount = data.reportsTo.filter(item => item.ApprovesTimesheets).length;

        let validationResult = {
            isValid: true,
        };

        if (notifiedUponApprovalCount > 1) {
            validationResult.isValid = false;
        }
        if (primaryCount > 1) {
            validationResult.isValid = false;
        }
        if (approvesLeavesCount > 1) {
            validationResult.isValid = false;
        }
        if (approvesTimesheetsCount > 1) {
            validationResult.isValid = false;
        }

        return validationResult;
    };
    //#endregion

    //#region Renderers
    const renderData = useCallback(({ item, index }) => {
        return (
            <Card
                style={[common.cardstyle]} >
                <Card.Content>
                    <ListView
                        item={item} index={index}
                        styles={styles}
                        handleDelete={handleDelete}
                        employees={employees}
                        control={control}
                        setValue={setValue}
                    />
                </Card.Content>
            </Card>
        )
    }, [fields, employees]);
    //#endregion

    //#region Loader
    if (isLoading)
        return (
            <View style={styles.loader}>
                <ActivityIndicator animating={true} color={colors.blue} />
            </View>
        )
    //#endregion

    return (
        <SafeAreaView style={styles.mainView}>
            <FlatList
                style={styles.mainView}
                data={fields}
                keyExtractor={(item) => item.rowguid}
                renderItem={renderData}
                scrollEnabled={true}
                refreshing={isLoading}
                ref={ref}
                ListHeaderComponent={
                    fields.length > 0 &&
                    <SafeAreaView>
                        <View style={[styles.row, { marginRight: '3%' }]}>
                            <CustomLabel
                                value={t('common:manager')}
                                type='SUBTITLE' />
                            <CustomLabel
                                value={t('common:notifiedUponApproval')}
                                type='SUBTITLE' />
                            <CustomLabel
                                value={t('common:primary')}
                                type='SUBTITLE' />
                            <CustomLabel
                                value={t('common:approvesLeaves')}
                                type='SUBTITLE' />
                            <CustomLabel
                                value={t('common:approvesTimesheets')}
                                type='SUBTITLE' />
                        </View>
                    </SafeAreaView>
                }
                initialNumToRender={10}
                ListEmptyComponent={
                    isLoading ?
                        <ActivityIndicator animating={true} color={colors.blue} />
                        :
                        <CustomLabel
                            value={t("common:noDataAvailable")}
                            type="NO_BRANCHES"
                        />
                }
            />
            <View style={[styles.buttons, styles.functionalityButtons]}>
                <CustomButton
                    text={t('common:new')}
                    onPress={() => { handleAddNew() }}
                    type='BUTTON_ADD'
                    textTop='0'
                    sz={15}
                />
                {fields.length > 0 &&
                    <CustomButton
                        text={t('common:save')}
                        onPress={handleSubmit(onSubmit)}
                        type="BUTTON_SAVE"
                        isGradient={true}
                        textTop='0'
                        sz={15}
                    />
                }
            </View>
        </SafeAreaView >
    );
}

const makeStyles = (colors, width) => StyleSheet.create({
    mainView: {
        flex: 1,
        marginHorizontal: 10,
    },
    row: {
        flex: 1,
        flexDirection: "row",
        alignItems: 'center',
    },
    margin: {
        marginHorizontal: 10,
    },
    item: {
        flex: 1,
        justifyContent: 'center',
    },
    error: {
        color: colors.validation_error,
        alignSelf: 'center',
        fontSize: 14,
    },
    functionalityButtons: {
        justifyContent: 'flex-end',
    },
    buttons: {
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'flex-end',
        width: '50%',
        alignSelf: 'flex-end',
    },
    loader: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '4%'
    },
});
